import { useId } from 'react'
import { Bookmark, BookmarkX } from 'lucide-react'
import { toast } from 'sonner'
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { Switch } from '@/components/ui/switch'
import { useSoftBookmark } from '@/hooks/use-soft-bookmark'
import { useSoftBookmarksEnabled } from '@/hooks/use-soft-bookmarks-enabled'
import { testIds } from '@/lib/test-ids'
import {
  bookmarkCurrentReadingPosition,
  clearDocumentSoftBookmark,
  jumpToSoftBookmark,
} from '@/lib/soft-bookmark-actions'

export function SoftBookmarkMenuLabelRow({
  enabled,
  onEnabledChange,
}: {
  enabled: boolean
  onEnabledChange: (enabled: boolean) => void
}) {
  const switchId = useId()
  return (
    <div
      className="flex items-center justify-between gap-2 px-3 py-1"
      data-testid={testIds.documentViewer.softBookmarksMenuLabel}
    >
      <label
        htmlFor={switchId}
        className="cursor-pointer text-[10px] font-medium uppercase tracking-wide text-muted-foreground/70"
      >
        Bookmark
      </label>
      <Switch
        id={switchId}
        checked={enabled}
        onCheckedChange={onEnabledChange}
        onClick={(event) => event.stopPropagation()}
        aria-label="Reading bookmarks"
        title={enabled ? 'Hide reading bookmarks' : 'Show reading bookmarks'}
        data-testid={testIds.documentViewer.softBookmarksEnabledSwitch}
      />
    </div>
  )
}

export function DocumentSoftBookmarkMenuSection({ documentId }: { documentId: string }) {
  const { enabled, setEnabled } = useSoftBookmarksEnabled()
  const { bookmark } = useSoftBookmark(documentId)

  return (
    <>
      <DropdownMenuSeparator />
      <SoftBookmarkMenuLabelRow enabled={enabled} onEnabledChange={setEnabled} />
      {enabled && (
        <>
          <DropdownMenuItem
            onClick={() => {
              bookmarkCurrentReadingPosition(documentId)
              toast.success('Bookmarked this spot', { duration: 2000 })
            }}
            data-testid={testIds.documentViewer.softBookmarkHereMenuItem}
          >
            <Bookmark className="h-4 w-4" />
            Bookmark here
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => jumpToSoftBookmark(documentId)}
            disabled={!bookmark}
            data-testid={testIds.documentViewer.softBookmarkJumpMenuItem}
          >
            <Bookmark className="h-4 w-4 fill-current" />
            Jump to bookmark
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => clearDocumentSoftBookmark(documentId)}
            disabled={!bookmark}
            data-testid={testIds.documentViewer.softBookmarkClearMenuItem}
          >
            <BookmarkX className="h-4 w-4" />
            Clear bookmark
          </DropdownMenuItem>
        </>
      )}
    </>
  )
}
